import { addDays, Duration, format, sub } from "date-fns";

import { DATE_FORMATS, MONTHS } from "@tm/isomorphic-utils";

// CALENDAR UTILITY FUNCTIONS
const INDIA_TZ_OFFSET_MINUTES = 330; // Asia/Kolkata is UTC+05:30 (no DST)
const INDIA_TZ_OFFSET_MS = INDIA_TZ_OFFSET_MINUTES * 60 * 1000;

type YMD = { year: number; monthIndex: number; day: number };

const getIndiaYmdFromNow = (): YMD => {
  const shifted = new Date(Date.now() + INDIA_TZ_OFFSET_MS);
  return {
    year: shifted.getUTCFullYear(),
    monthIndex: shifted.getUTCMonth(),
    day: shifted.getUTCDate(),
  };
};

const getIndiaYmdFromUnix = (unixSeconds: number): YMD => {
  const shifted = new Date(unixSeconds * 1000 + INDIA_TZ_OFFSET_MS);
  return {
    year: shifted.getUTCFullYear(),
    monthIndex: shifted.getUTCMonth(),
    day: shifted.getUTCDate(),
  };
};

export const getIndiaTodayDate = (): Date => {
  const { year, monthIndex, day } = getIndiaYmdFromNow();
  return new Date(year, monthIndex, day);
};

export const randomDayData = () => {
  const min = 10000;
  const max = 15000;
  const randomNum = Math.floor(Math.random() * (max - min + 1) + min);

  return {
    price: `₹${randomNum}`,
    class: randomNum <= 12000 ? "greenText" : "redText",
    isSoldOut: randomNum > 12000 && randomNum < 13000,
    availableOnPreviousDate: randomNum >= 13000 && randomNum <= 13500,
  };
};

export const convertUnixToDate = (unix?: number | null) => {
  if (unix && typeof unix === "number" && !Number.isNaN(unix)) {
    const { year, monthIndex, day } = getIndiaYmdFromUnix(unix);
    return new Date(year, monthIndex, day);
  }
  return;
};

export const convertDateToUnix = (date: Date) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) return 0;
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();
  const utcMs = Date.UTC(y, m, d, 0, 0, 0, 0) - INDIA_TZ_OFFSET_MS;
  return Math.floor(utcMs / 1000);
};

export const formatDate = (date: Date | string, dateFormat: DATE_FORMATS) => {
  if (date) {
    try {
      date = new Date(date);
      return format(date, dateFormat);
    } catch (error) {
      /* istanbul ignore next */
      console.log("Error in formatting given date", `${date} | ${dateFormat}`);
    }
  }
  return "";
};

export const addDaysToDate = (date: Date, days = 1) => {
  try {
    return addDays(date, days);
  } catch (error) {
    /* istanbul ignore next */
    console.log("Error in adding given days to date", `${date} | ${days}`);
  }
  /* istanbul ignore next */
  return;
};

export const subtractDurationFromDate = (
  date: Date | string,
  duration: Duration
) => {
  if (date) {
    try {
      date = new Date(date);
      return sub(date, duration);
    } catch (error) {
      /* istanbul ignore next */
      console.log(
        "Error in subtracting given duration from date",
        `${date} | ${duration}`
      );
    }
  }
  /* istanbul ignore next */
  return new Date();
};

export const subtractYrsFromDate = (date: Date | string, years: number) => {
  if (date && years) {
    try {
      return subtractDurationFromDate(date, { years });
    } catch (error) {
      /* istanbul ignore next */
      console.log(
        "Error in subtracting given years from date",
        `${date} | ${years}`
      );
    }
  }
  /* istanbul ignore next */
  return new Date();
};

export const calendarLabelFormatter = (dateObj: any) => {
  const indiaNow = new Date(Date.now() + INDIA_TZ_OFFSET_MS);
  return dateObj
    ? `${dateObj.getDate()} ${MONTHS[dateObj.getMonth()]}' ${dateObj
        .getFullYear()
        .toString()
        .slice(-2)}`
    : `${indiaNow.getUTCDate()} ${MONTHS[indiaNow.getUTCMonth()]}' ${indiaNow
        .getUTCFullYear()
        .toString()
        .slice(-2)}`;
};
